import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { getMessagingErrorMessage } from "messaging/_api/messaging-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import UserInformation from "users/information/user-information";
import NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";
import useInfiniteNetworkConnections from "users/networking/connections/_hooks/use-infinite-network-connections";
import useSendToNetworkContact from "users/networking/connections/_views/components/use-send-to-network-contact";

type SendResultToContactDialogProps = {
  open: boolean;
  onClose: () => void;
  resultName: string;
  /** The profile's full address. A chat renders markdown, and only an absolute URL becomes a link. */
  profileUrl: string;
};

/**
 * The message a contact receives: the sender's note, if any, then the result's name linked to its
 * profile. A bracket in the name would end the link text early, so it is escaped.
 */
export function getResultLinkMessage(note: string, resultName: string, profileUrl: string): string {
  const link = `[${resultName.replaceAll(/[[\]\\]/g, String.raw`\$&`)}](${profileUrl})`;
  return note.trim() ? `${note.trim()}\n\n${link}` : link;
}

const SEARCH_DELAY_MILLISECONDS = 300;
// How near the end of the list, in pixels, a scroll loads the next page.
const LOAD_MORE_THRESHOLD_PIXELS = 40;

// The server searches name, email and firm, so the list shows what it sends without filtering again.
const showServerMatches = (contacts: UserInformation[]) => contacts;

/**
 * Sends a marketplace result to someone in the viewer's network, as a message in the chat the two
 * already share (#2412). Copy link stays beside it for sending the address anywhere else.
 */
export default function SendResultToContactDialog(props: Readonly<SendResultToContactDialogProps>) {
  const { open, onClose, resultName, profileUrl } = props;
  const titleId = React.useId();

  const [searchText, setSearchText] = React.useState<string>("");
  const [search, setSearch] = React.useState<string>("");
  React.useEffect(() => {
    const handle = globalThis.setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MILLISECONDS);
    return () => globalThis.clearTimeout(handle);
  }, [searchText]);
  const query = React.useMemo(() => new NetworkConnectionQuery({ query: search }), [search]);
  const { connections, isLoading, loadMore } = useInfiniteNetworkConnections(query, { skip: !open });
  const send = useSendToNetworkContact();

  const [contact, setContact] = React.useState<UserInformation | null>(null);
  const [note, setNote] = React.useState<string>("");
  const [isSending, setIsSending] = React.useState<boolean>(false);

  function close() {
    setContact(null);
    setNote("");
    setSearchText("");
    setSearch("");
    onClose();
  }

  function loadMoreAtEnd(event: React.UIEvent<HTMLElement>) {
    const list = event.currentTarget;
    if (list.scrollHeight - list.clientHeight - list.scrollTop <= LOAD_MORE_THRESHOLD_PIXELS) loadMore();
  }

  async function handleSend() {
    if (!contact) return;
    const contactName = contact.name.fullName;
    const content = getResultLinkMessage(note, resultName, profileUrl);
    setIsSending(true);
    try {
      await send(contact, content);
    } catch (error) {
      setIsSending(false);
      enqueueSnackbar(getMessagingErrorMessage(error, "Couldn't send the link. Please try again."), {
        variant: "error",
      });
      return;
    }
    setIsSending(false);
    enqueueSnackbar(`Sent to ${contactName}`, { variant: "success" });
    close();
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs" aria-labelledby={titleId}>
      <DialogTitle id={titleId}>Send {resultName} to a contact</DialogTitle>
      <Fields>
        <Autocomplete
          options={connections.data}
          value={contact}
          onChange={(_event, value) => setContact(value)}
          onInputChange={(_event, value, reason) => {
            // Choosing a contact writes their name into the field. That is not a search.
            if (reason === "input" || reason === "clear") setSearchText(value);
          }}
          getOptionLabel={(option) => option.name.fullName}
          isOptionEqualToValue={(option, value) => option.id.isEqualTo(value.id)}
          filterOptions={showServerMatches}
          loading={isLoading}
          slotProps={{ listbox: { onScroll: loadMoreAtEnd } }}
          noOptionsText="Nobody in your network matches"
          renderOption={(optionProps, option) => {
            const { key, ...liProps } = optionProps;
            return (
              <ContactOption key={key} {...liProps}>
                <IndividualAvatar individual={option.toIndividual()} />
                <span>
                  {option.name.fullName}
                  {option.companyName && <ContactFirm>{option.companyName}</ContactFirm>}
                </span>
              </ContactOption>
            );
          }}
          renderInput={(params) => <TextField {...params} label="Contact" />}
        />
        <TextField
          label="Message (optional)"
          multiline
          minRows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Fields>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disableElevation disabled={!contact || isSending} onClick={handleSend}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Fields = styled(DialogContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  // MUI zeroes the top padding of content that follows a title, which puts the first field's
  // floating label on the title's bottom edge. The doubled class outranks that rule.
  "&&": { paddingTop: theme.spacing(1) },
}));

const ContactOption = styled("li")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
}));

const ContactFirm = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  display: "block",
}));
